import { scoreVoicing } from '@tunasong/music-lib'
import type { VoiceLeadingQuizQuestion, VoiceLeadingQuizVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { VoicingPianoRoll } from './voicing-piano-roll.js'
import styles from './voice-leading-quiz-feedback.module.css'

export interface VoiceLeadingQuizFeedbackProps {
  question: VoiceLeadingQuizQuestion
  verdict: VoiceLeadingQuizVerdict
  onReplayVoicing: (slot: 'A' | 'B') => void
  onNext: () => void
}

const SLOTS = ['A', 'B'] as const

/**
 * Voice-Leading Quiz feedback panel. Reveals both voicings in the order they
 * were presented (slot A / slot B), tags the smoother one, gives an independent
 * replay button per voicing (Bowie §19), and shows the lexicon explainer.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §5
 */
export const VoiceLeadingQuizFeedback: FC<VoiceLeadingQuizFeedbackProps> = props => {
  const { question, verdict, onReplayVoicing, onNext } = props
  const { voicingPair, presentationOrder, idiom } = question
  const explainerCopy = useTheoryCopy('vl-quiz-feedback')

  const labels = pairLabels(voicingPair.pair)

  return (
    <section className={styles.panel} aria-label="Voice-Leading Quiz feedback">
      <div className={styles.verdictLine}>
        {verdict.correct ? (
          <span className={styles.correct}>Correct! Voicing {verdict.acceptedSlot} was smoother.</span>
        ) : (
          <span className={styles.incorrect}>
            Not quite — Voicing {verdict.acceptedSlot} was the smoother one.
          </span>
        )}
      </div>

      <div className={styles.comparison}>
        {SLOTS.map((slot, i) => {
          const which = presentationOrder[i]
          const voicing = which === 'smooth' ? voicingPair.smooth : voicingPair.awkward
          const score = scoreVoicing(voicing, { idiom, pair: voicingPair.pair })
          const isAccepted = slot === verdict.acceptedSlot
          return (
            <section key={slot} className={styles.voicingCard} aria-label={`Voicing ${slot}`}>
              <header className={styles.voicingHeader}>
                <span className={styles.voicingTitle}>
                  Voicing {slot}
                  {isAccepted ? ' — smoother' : ''}
                </span>
                <span className={styles.scoreBadge}>{formatScore(score.total)}</span>
              </header>
              <VoicingPianoRoll voicing={voicing} columnLabels={labels} idiom={idiom} />
              <Button
                type="button"
                variant="outlined"
                onClick={() => onReplayVoicing(slot)}
                data-testid={`vl-replay-${slot}`}
              >
                Replay
              </Button>
            </section>
          )
        })}
      </div>

      {explainerCopy?.body ? <p className={styles.explainer}>{explainerCopy.body}</p> : null}

      <Button type="button" variant="contained" onClick={onNext}>
        Next
      </Button>
    </section>
  )
}

const formatScore = (total: number): string => (total > 0 ? `+${total}` : `${total}`)

const pairLabels = (pair: VoiceLeadingQuizQuestion['voicingPair']['pair']): readonly [string, string] => {
  const [first, second] = pair.split('-')
  return [first, second]
}
